import { useState } from "react";
import { useQueryClient } from "react-query";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useUIDSeed } from "react-uid";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { useDrawerState } from "@hmn/coolinarika-web-core/context/Drawer";
import { slugParser } from "@hmn/coolinarika-web-core/helpers";
import {
    getListElastic,
    useNotification,
    useOneElastic,
    useTaxonomyMutate,
} from "@hmn/coolinarika-web-core/hooks";
import { validNotificationTypes } from "@hmn/coolinarika-web-core/hooks/useNotification";
import useTaxonMutate, { MutationAction } from "@hmn/coolinarika-web-core/hooks/useTaxonMutate";

import { Button, buttonSizes, buttonVariants } from "../../../Button";
import { Notification } from "../../../Collections/components/Notification";
import { DrawerBody } from "../../../Drawer";
import { withErrorBoundary } from "../../../ErrorBoundary";
import { pillVariants, TagPill } from "../Pill";
import { TagPillsList } from "../PillsList";
import styles from "./TagsAdd.style";

const NOTIFICATION_ID = "tags-notification";

const TagsAddStyled = styled.div((props) => ({ ...styles(props) }));

const getTagSuggestionOptions = async (inputValue) => {
    const { data } = await getListElastic({
        resource: "tags/coolinarika-content-tags/pretrazivanje",
        params: {
            query: {
                search: inputValue,
            },
        },
        enabled: !!inputValue,
    });

    const { pages } = data || {};

    if (Array.isArray(pages) && pages.length > 0) {
        return pages[0].map(({ id, suggestion }) => ({
            id,
            label: suggestion,
            value: suggestion,
            suggestion,
        }));
    }

    return [];
};

/**
 * Unsluggifies and capitalizes tag suggestions.
 *
 * @param {*} title
 * @return {*}
 */
const prettifySuggestion = (suggestion) => {
    if (!suggestion || typeof suggestion !== "string") {
        return "";
    }

    const result = suggestion.replace(/-/g, " ");
    return `${result.charAt(0).toUpperCase()}${result.substr(1).toLowerCase()}`;
};

const getLabelAndValue = ({ suggestion, label, value }) =>
    prettifySuggestion(suggestion || label || value);

const formatCreateLabel = (value) => `Napravi novi tag "${value}"`;

const noOptionsMessage = () => "Nema rezultata";

const loadingMessage = () => "Učitavanje...";

const TagsAdd = ({ tagBasePath }) => {
    const [tagsValue, setTagsValue] = useState(null);

    const { actions: tagActions } = useTaxonomyMutate({
        resource: "tags",
        entity: "coolinarika-content-tags",
        bag: "coolinarika-tags",
    });

    const queryClient = useQueryClient();

    const { contentId, content: entity } = useDrawerState();
    const { sendNotification } = useNotification({
        id: NOTIFICATION_ID,
    });

    // Tags applied by current user
    const {
        taxons: appliedUserTags,
        actions,
        queryKey: appliedQueryKey,
    } = useTaxonMutate({
        entity,
        entityId: contentId,
        taxonomy: "coolinarika-tags",
        bag: "coolinarika-content-tags",
    });

    // All tags on this recipe
    const { data: tagsData, queryKey: allQueryKey } = useOneElastic({
        resource: "tags",
        id: contentId,
        enabled: !!contentId,
    });

    const onSuccess = (newItem, ...rest) => {
        const [mutationInfo] = rest;
        const { action } = mutationInfo;
        if (action) {
            if (action === MutationAction.CREATE) {
                sendNotification({
                    message: `Tag je dodan!`,
                    type: validNotificationTypes.SUCCESS,
                });

                queryClient.setQueryData(allQueryKey, [
                    ...tagsData,
                    {
                        id: newItem.taxon.id,
                        count: 1,
                        data: [
                            {
                                slug: newItem.taxon.slug,
                                title: newItem.taxon.title,
                                id: newItem.id,
                            },
                        ],
                    },
                ]);
            } else if (action === MutationAction.DELETE) {
                sendNotification({
                    message: `Tag je uspješno maknut!`,
                    type: validNotificationTypes.SUCCESS,
                });

                const mutateData = tagsData.filter((item) => item.data[0]?.id !== newItem.id);
                queryClient.setQueryData(allQueryKey, [...mutateData]);
            }
        }

        // Give PIO & Elastic enough time to sync
        setTimeout(() => {
            queryClient.invalidateQueries([appliedQueryKey, allQueryKey]);
        }, 1500);
    };
    const onError = () => {
        sendNotification({
            message: `Nešto je pošlo po zlu!`,
            type: validNotificationTypes.ERROR,
        });
    };

    const handleTagAdd = async () => {
        if (!tagsValue || !tagsValue?.value?.length) {
            return;
        }

        const existingTag = Object.keys(tagsValue || {})?.length && tagsValue?.id && tagsValue;

        if (existingTag) {
            const { uuid: suggestedTagUuid } =
                (existingTag?.id && slugParser(existingTag.id)) || {};

            const isAlreadyApplied = appliedUserTags.some(
                (item) => item?.slug === existingTag?.value?.toLowerCase(),
            );

            if (isAlreadyApplied) {
                sendNotification({
                    message: `Tag je već dodijeljen!`,
                    type: validNotificationTypes.ERROR,
                });
            } else {
                actions.create({
                    data: {
                        taxonId: suggestedTagUuid,
                    },
                    options: {
                        onSuccess,
                        onError,
                    },
                });
            }
        } else {
            tagActions.create({
                data: { title: tagsValue?.value },
                options: {
                    onSuccess: (createdTag) => {
                        actions.create({
                            data: {
                                taxonId: createdTag?.data?.id,
                            },
                            options: {
                                onSuccess,
                                onError,
                            },
                        });
                    },
                    onError,
                },
            });
        }

        setTagsValue(null);
    };

    const handleDeleteTag = (tagId) => {
        const tagTaxon = appliedUserTags.find((item) => item.id === tagId);

        if (tagTaxon) {
            if (tagTaxon?.content_taxon_id) {
                actions.delete({
                    data: {
                        taxonId: tagTaxon?.content_taxon_id,
                    },
                    options: {
                        onSuccess,
                        onError,
                    },
                });
            } else {
                onError();
            }
        } else {
            onError();
        }
    };

    const uid = useUIDSeed();

    const handleTagChange = (value) => {
        if (value) {
            setTagsValue(value);
        }
    };

    return (
        <>
            <DrawerBody>
                <TagsAddStyled>
                    <div className="tagSelectContainer">
                        <AsyncCreatableSelect
                            name="tagSearch"
                            placeholder="Upiši tag..."
                            id="tagSearch"
                            instanceId="tagSearch"
                            backspaceRemovesValue
                            onChange={handleTagChange}
                            isClearable
                            isSearchable
                            // className="tagSelectContainer"
                            classNamePrefix="tagSelect"
                            escapeClearsValue
                            allowCreateWhileLoading
                            createOptionPosition="last"
                            openMenuOnClick={false}
                            openMenuOnFocus={false}
                            formatCreateLabel={formatCreateLabel}
                            loadOptions={getTagSuggestionOptions}
                            getOptionLabel={getLabelAndValue}
                            getOptionValue={getLabelAndValue}
                            styles={{
                                option: (providedStyles, props) => ({
                                    ...providedStyles, // To keep the default style
                                    // eslint-disable-next-line no-underscore-dangle, react/prop-types
                                    ...(props.data.__isNew__ && {
                                        fontWeight: "bold !important",
                                        color: "black !important",
                                    }),
                                }),
                            }}
                            components={{ IndicatorsContainer: () => null }}
                            noOptionsMessage={noOptionsMessage}
                            loadingMessage={loadingMessage}
                        />
                        <Button
                            title="Dodaj tag"
                            className="tagSelectButton"
                            variant={buttonVariants.QUATERNARY}
                            size={buttonSizes.SMALL}
                            disabled={!tagsValue}
                            onClick={handleTagAdd}
                        >
                            Dodaj tag
                        </Button>
                    </div>
                </TagsAddStyled>

                <TagPillsList
                    title="Tvoji tagovi"
                    hasMarginTop
                    variant={pillVariants.SECONDARY}
                    limit={20}
                    hasShowMoreButton={appliedUserTags?.length > 20}
                >
                    {appliedUserTags?.map((tag, index) => (
                        <TagPill
                            key={uid(`${tag?.id}-${index}`)}
                            label={tag?.title}
                            value={tag?.id}
                            id={tag?.id}
                            onClick={() => handleDeleteTag(tag?.id)}
                            hasRemoveIcon
                            variant={pillVariants.SECONDARY}
                        />
                    ))}
                </TagPillsList>

                {tagsData?.length > 0 && (
                    <TagPillsList
                        title="Tagovi korisnika"
                        hasMarginTop
                        paddingBottom={40}
                        limit={20}
                        hasShowMoreButton={tagsData?.length > 20}
                    >
                        {tagsData?.map((tag, index) => {
                            const { count, data } = tag;
                            const [itemData] = data;
                            return (
                                <TagPill
                                    count={count}
                                    key={uid(itemData?.id || index)}
                                    href={`/inspiracija/${itemData?.slug}-${itemData?.id}/`}
                                    name={itemData?.title}
                                    label={itemData?.title}
                                    isRounded
                                />
                            );
                        })}
                    </TagPillsList>
                )}
            </DrawerBody>
            <Notification uid={NOTIFICATION_ID} />
        </>
    );
};

TagsAdd.propTypes = {
    tagBasePath: PropTypes.string,
};

TagsAdd.defaultProps = {
    tagBasePath: "/user-profile-private/tags",
};

export default withErrorBoundary(TagsAdd);
